import React, { useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { HelpCircle, Book, User, Globe, MessageCircle } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
  icon: React.ReactNode;
}

export function FAQ() {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  
  const categories = [
    { id: 'all', name: 'All Questions', icon: <HelpCircle className="h-5 w-5" /> },
    { id: 'basics', name: 'Basic Knowledge', icon: <Book className="h-5 w-5" /> },
    { id: 'naming', name: 'Naming Rules', icon: <User className="h-5 w-5" /> },
    { id: 'culture', name: 'Cultural Traditions', icon: <Globe className="h-5 w-5" /> },
    { id: 'usage', name: 'Usage Guide', icon: <MessageCircle className="h-5 w-5" /> },
  ];

  const faqItems: FAQItem[] = [
    // How to Choose a Chinese Name Section
    {
      question: "How to choose a Chinese name that suits me?",
      answer: "Choosing a Chinese name involves several considerations: 1. Consider the meaning and cultural significance of characters 2. Ensure proper pronunciation and tone combinations 3. Think about how it sounds with your surname 4. Choose characters that reflect your personality or aspirations 5. Consult with native speakers for cultural appropriateness. Our AI generator takes all these factors into account automatically.",
      category: "naming",
      icon: <User className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "Can foreigners have Chinese names?",
      answer: "Absolutely! Foreigners can and often do adopt Chinese names for various reasons - cultural connection, business purposes, studying Chinese, or personal interest. Chinese names for foreigners are very common and widely accepted. Many international students, business professionals, and people with cultural connections to China choose Chinese names to facilitate communication and show respect for Chinese culture.",
      category: "culture",
      icon: <Globe className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "How to pronounce Chinese names correctly?",
      answer: "Chinese names are pronounced using pinyin with four main tones: 1. First tone (flat/high) 2. Second tone (rising) 3. Third tone (falling then rising) 4. Fourth tone (falling). Each tone changes the meaning completely. Practice with native speakers or use our pronunciation guide with audio examples. Pay attention to tone marks in pinyin - they're crucial for correct pronunciation.",
      category: "basics",
      icon: <Book className="h-6 w-6 text-indigo-600" />
    },

    // Chinese Name Pronunciation Guide Section
    {
      question: "What do Chinese names mean and how are meanings determined?",
      answer: "Chinese names carry deep meanings derived from the individual characters used. Each character has its own meaning, and combinations create layered significance. Common themes include: virtues (wisdom, kindness), natural elements (water, mountain), aspirations (success, harmony), and cultural values. Parents traditionally choose characters that express their hopes for their child's future and reflect family values.",
      category: "culture",
      icon: <Globe className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "How are Chinese names chosen by families?",
      answer: "Chinese name selection is often a family affair involving multiple generations. The process typically includes: 1. Consulting family elders for wisdom 2. Considering generational naming patterns 3. Analyzing the child's birth time for auspicious elements 4. Choosing characters with positive meanings 5. Ensuring good pronunciation and writing. Some families also consult traditional practices like the Five Elements theory or Chinese astrology.",
      category: "culture",
      icon: <Globe className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "Chinese naming rules and taboos - what should I avoid?",
      answer: "Important naming rules in Chinese culture include: 1. Avoid characters with negative meanings or associations 2. Don't use names of famous historical figures inappropriately 3. Avoid characters that are too difficult to write or pronounce 4. Consider tone combinations for harmonious sound 5. Respect generational naming traditions if applicable 6. Avoid characters associated with death, illness, or bad fortune.",
      category: "naming",
      icon: <User className="h-6 w-6 text-indigo-600" />
    },

    // Chinese Name Structure Explained Section
    {
      question: "How do Chinese names work - what's the structure?",
      answer: "Chinese names follow a specific structure: Family name (surname) + Given name. The surname comes first (opposite to Western names) and is usually one character. The given name follows and can be one or two characters. For example, in '李明' (Li Ming), '李' is the surname and '明' is the given name. This structure reflects the cultural emphasis on family before individual identity.",
      category: "basics",
      icon: <Book className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "Why do some Chinese names have two characters while others have three?",
      answer: "Chinese name length depends on the given name: Surname (1 character) + Given name (1-2 characters) = 2-3 total characters. Two-character names have a one-character given name, while three-character names have a two-character given name. Two-character given names are more common as they allow for richer meaning combinations and are considered more formal and complete.",
      category: "basics",
      icon: <Book className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "Chinese name meaning lookup - how can I find the meaning of a Chinese name?",
      answer: "To find Chinese name meanings: 1. Break down the name into individual characters 2. Look up each character's meaning in a Chinese dictionary 3. Consider how the characters work together 4. Research cultural and historical context 5. Consult native speakers for nuanced understanding. Our name generator provides detailed explanations of each character's meaning and cultural significance.",
      category: "usage",
      icon: <MessageCircle className="h-6 w-6 text-indigo-600" />
    },

    // Usage Guide Questions
    {
      question: "How to use our Chinese name generator effectively?",
      answer: "To get the best results from our generator: 1. Enter your full English name accurately 2. Consider the gender-specific suggestions provided 3. Read the cultural explanations for each suggestion 4. Listen to pronunciation guides 5. Save multiple options for consideration 6. Share with Chinese-speaking friends for feedback. The AI analyzes your name's meaning and creates culturally appropriate Chinese equivalents.",
      category: "usage",
      icon: <MessageCircle className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "Can I use the generated Chinese name professionally?",
      answer: "Yes, generated Chinese names can be used professionally, especially in: 1. Business contexts involving Chinese-speaking clients 2. Academic or educational settings 3. Cultural exchange programs 4. Social media and online platforms 5. Personal branding in Chinese markets. However, we recommend having native speakers verify the name's appropriateness for your specific professional context.",
      category: "usage",
      icon: <MessageCircle className="h-6 w-6 text-indigo-600" />
    },
    {
      question: "Chinese name for foreigners - special considerations?",
      answer: "Foreigners choosing Chinese names should consider: 1. Cultural sensitivity and appropriateness 2. Ease of pronunciation for Chinese speakers 3. Professional vs. casual usage contexts 4. Regional preferences (Mainland, Taiwan, Hong Kong) 5. Written form (simplified vs. traditional characters) 6. Connection to your original name's meaning. Many foreigners successfully adopt Chinese names for better cultural integration.",
      category: "culture",
      icon: <Globe className="h-6 w-6 text-indigo-600" />
    }
  ];

  return (
    <>
      <SeoMeta
        title="Chinese Names FAQ - How to Choose & Pronounce Chinese Names"
        description="Have questions about Chinese names? Get expert answers to common questions: How to choose a Chinese name? Can foreigners have Chinese names? How to pronounce Chinese names correctly? What do Chinese names mean? Learn naming rules, pronunciation tips, cultural considerations, and avoid common mistakes. Everything you need to know before selecting your Chinese name."
        keywords="how to choose a chinese name, how to pronounce chinese names, what do chinese names mean, chinese name meaning lookup, can i have a chinese name, chinese name pronunciation guide, chinese naming rules, how are chinese names chosen, chinese name for foreigners, chinese name questions, chinese name help"
        pathname="/faq"
      />
      <div className="container mx-auto px-4 py-8">
        {/* 标题部分 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Chinese Names Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get comprehensive answers about Chinese names, pronunciation, cultural meanings, and how to choose the perfect Chinese name for yourself
          </p>
        </div>

        {/* How to Choose a Chinese Name Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How to Choose a Chinese Name
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Essential guidance for selecting an appropriate and meaningful Chinese name
            </p>
          </div>
        </div>

        {/* Chinese Name Pronunciation Guide Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese Name Pronunciation Guide
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Master the art of pronouncing Chinese names correctly with tone awareness
            </p>
          </div>
        </div>

        {/* Chinese Naming Rules and Taboos Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Chinese Naming Rules and Taboos
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understand cultural guidelines and avoid common mistakes in Chinese naming
            </p>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-6">
            Browse by Category
          </h3>
          
          {/* 分类过滤器 */}
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors duration-200 ${
                  activeCategory === category.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category.icon}
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Questions with H3 Headers */}
        <div className="space-y-6 max-w-4xl mx-auto">
          {/* Can Foreigners Have Chinese Names */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Can Foreigners Have Chinese Names?
            </h3>
          </div>

          {/* What Do Chinese Names Mean */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              What Do Chinese Names Mean?
            </h3>
          </div>

          {/* How to Pronounce Chinese Names Correctly */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              How to Pronounce Chinese Names Correctly
            </h3>
          </div>

          {/* Chinese Name Structure Explained */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Chinese Name Structure Explained
            </h3>
          </div>

          {/* FAQ卡片列表 */}
          {faqItems
            .filter(item => activeCategory === 'all' || item.category === activeCategory)
            .map((item, index) => (
              <Card 
                key={index} 
                className="hover:shadow-lg transition-all duration-200 hover:border-indigo-200"
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {item.icon}
                    <div>
                      <h4 className="text-xl font-semibold mb-3">{item.question}</h4>
                      <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>

        {/* Additional Resources Section */}
        <div className="mt-16 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">
              Still Have Questions?
            </h2>
            <p className="text-lg text-gray-600 mt-4">
              Explore our comprehensive resources for more information about Chinese names
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <a 
              href="/chinese-name-generator" 
              className="block p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Try Our Generator</h3>
              <p className="text-gray-600">Generate your personalized Chinese name with detailed explanations</p>
            </a>

            <a 
              href="/cultural-insights" 
              className="block p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Cultural Guide</h3>
              <p className="text-gray-600">Learn about Chinese naming traditions and cultural significance</p>
            </a>

            <a 
              href="/chinese-last-names" 
              className="block p-6 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Surnames Guide</h3>
              <p className="text-gray-600">Explore the meanings and origins of Chinese family names</p>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
